import { type FC } from 'react';
import { products } from '@/mocks/products';

interface UnderTwentyDealsProps {
  onProductClick: (link: string) => void;
}

const UnderTwentyDeals: FC<UnderTwentyDealsProps> = ({ onProductClick }) => {
  const deals = products
    .filter((p) => p.price < 20 && p.inStock)
    .sort((a, b) => a.price - b.price);

  return (
    <section className="px-4 py-10 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-rose-500 rounded-xl shadow-md">
              <i className="ri-price-tag-3-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight">
                Under $20 Deals
              </h2>
              <p className="text-sm text-gray-500">Budget-friendly picks your cat will love</p>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-600 px-4 py-2 rounded-full text-sm font-bold">
            <i className="ri-sparkling-line"></i>
            {deals.length} items under $20
          </div>
        </div>

        {/* Deals Banner */}
        <div className="relative bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl p-5 mb-6 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/20"></div>
          <div className="absolute -right-6 -top-6 text-9xl opacity-10">🏷️</div>
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-white/80 text-xs font-semibold uppercase tracking-widest mb-1">
                Paw-Approved Budget Picks
              </p>
              <h3 className="text-white text-xl md:text-2xl font-black">
                Great quality doesn't have to cost a lot 🐾
              </h3>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 text-center">
                <div className="text-white text-2xl font-black">$4.99</div>
                <div className="text-white/80 text-xs">Lowest price</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-2 text-center">
                <div className="text-white text-2xl font-black">{deals.length}</div>
                <div className="text-white/80 text-xs">Products</div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4" data-product-shop>
          {deals.map((product) => (
            <div
              key={product.id}
              onClick={() => onProductClick(product.link)}
              className="bg-white border-2 border-gray-100 hover:border-rose-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 hover:scale-[1.02] cursor-pointer group"
            >
              <div className="relative overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://public.readdy.ai/ai/img_res/3bef5e4cde10ebd7944becfba93f5226.jpg';
                  }}
                  className="w-full h-32 md:h-36 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
                {/* Price Tag */}
                <div className="absolute top-2 right-2 bg-rose-500 text-white text-xs font-black px-2 py-1 rounded-full shadow-md">
                  ${product.price}
                </div>
                {product.badge && (
                  <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                    product.badge === 'SALE' ? 'bg-rose-500 text-white' :
                    product.badge === 'BESTSELLER' ? 'bg-amber-500 text-white' :
                    product.badge === 'TOP RATED' ? 'bg-teal-500 text-white' :
                    'bg-gray-700 text-white'
                  }`}>
                    {product.badge}
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1 mb-1.5">
                  <i className="ri-medal-line text-amber-500 text-xs"></i>
                  <span className="text-xs font-semibold text-amber-600">Highly Rated on Chewy</span>
                </div>
                <h4 className="font-semibold text-gray-800 text-xs leading-snug line-clamp-2 mb-2 min-h-[32px]">
                  {product.name}
                </h4>
                <div className="flex items-center gap-1 mb-2">
                  <span className="text-xs text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full">
                    {product.category}
                  </span>
                </div>
                <button className="w-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold py-2 px-3 rounded-full transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-1 min-h-[34px]">
                  <i className="ri-shopping-bag-3-line"></i>
                  Get It
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            <i className="ri-information-line mr-1"></i>
            All prices are from Chewy. Prices may vary. We earn a small commission on purchases — at no extra cost to you.
          </p>
        </div>
      </div>
    </section>
  );
};

export default UnderTwentyDeals;